import * as React from 'react';
import { useSelector } from 'react-redux';

import { getSubmissionDetailsLoading, getSovLocations } from 'application/redux/selectors';
import { CircularLoader } from 'components/Loader';
import LrGrid from 'components/LrGrid';
import { UwGridRow, UwReview, UwLocationReview, UwGLLocationReview } from 'types/underwriting.type';

import { columnDefs, defaultColDef } from './GridDefs';
import { Page, Spacing } from '../../../Layout';
import { LabeledData } from '../../../Input';
import { formatDollar, formatPercent } from '../../../../helpers/formatters';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';

interface Props {
    selectedUWReview: UwReview;
    isLoading: boolean;
}

const UwGLSummary: React.FunctionComponent<Props> = (props) => {
    const { isLoading, selectedUWReview } = props;

    // Global State
    const submissionIsLoading = useSelector(getSubmissionDetailsLoading);
    const locations = useSelector(getSovLocations);

    // Local State
    const [gridData, setGridData] = React.useState([] as UwGridRow[]);

    const createGridData = () => {
        if (!locations.length || !selectedUWReview.locationReviews) return;

        const data = selectedUWReview.locationReviews.map(
            ({
                baseRatedPremium,
                irpm,
                ratingScore,
                scoreIrpm,
                experienceCRDB,
                totalPremium,
                locationName,
            }: UwGLLocationReview) => {
                return {
                    locationName: locationName || '--',
                    baseRatedPremium,
                    ratingScore,
                    irpm,
                    scoreIrpm,
                    experienceCRDB,
                    totalPremium,
                };
            }
        );
        setGridData(data);
    };

    React.useEffect(createGridData, [selectedUWReview, locations]);

    if (isLoading || submissionIsLoading) return <CircularLoader />;

    const HNOPremium =
        (selectedUWReview?.underwriterReview?.HNOPremium || 0) *
        (1 + (selectedUWReview?.underwriterReview?.triaPercentage || 0) / 100);

    return (
        <Page title={'GL Underwriting Summary'}>
            <Grid container spacing={3}>
                <Grid>
                    <LabeledData
                        title={'Agent Commission'}
                        value={
                            formatPercent(selectedUWReview?.underwriterReview?.brokerCommissionPercentage, 1) || '--'
                        }
                    />
                </Grid>
                <Grid>
                    <LabeledData title={'HNO Premium'} value={formatDollar(HNOPremium) || '--'} />
                </Grid>
                <Grid>
                    <LabeledData
                        title={'Policy Fee'}
                        value={formatDollar(selectedUWReview?.underwriterReview?.policyFee) || '--'}
                    />
                </Grid>
                <Grid>
                    <LabeledData
                        title={'Professional Fee'}
                        value={formatDollar(selectedUWReview?.underwriterReview?.professionalFee) || '--'}
                    />
                </Grid>
                <Grid>
                    <LabeledData
                        title={'Inspection Fee'}
                        value={formatDollar(selectedUWReview?.underwriterReview?.inspectionFee) || '--'}
                    />
                </Grid>
            </Grid>
            <Spacing height={'24px'} />
            <Typography variant={'h5'}>Location Reviews</Typography>
            <LrGrid
                defaultColDef={defaultColDef}
                columnDefs={columnDefs}
                rowData={gridData}
                height={'25vh'}
                // domLayout={'autoHeight'}
                sizeToFit
                autosize
            />
        </Page>
    );
};

export default React.memo(UwGLSummary);
